import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { fetchProduct } from "../lib/api";
import Overline from "../components/Overline";
import Reveal from "../components/Reveal";
import PatternStrip from "../components/PatternStrip";
import { PHOTOGRAPHY } from "../lib/constants";

const Product = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [weight, setWeight] = useState("500g");
  const [error, setError] = useState(false);

  useEffect(() => {
    setProduct(null);
    setError(false);
    fetchProduct(slug)
      .then(setProduct)
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
        <Overline>404</Overline>
        <h1 className="font-serif-display text-5xl text-elle-green mt-4">
          Coffee not found
        </h1>
        <Link to="/shop" className="inline-flex items-center gap-2 mt-10 link-underline text-[11px] tracking-overline uppercase text-elle-green">
          <ArrowLeft size={14} /> Back to shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-elle-soft" data-testid="product-loading">Loading…</div>;
  }

  const prices = {
    "500g": product.price_500g.toFixed(0),
    "1kg": product.price_1kg.toFixed(0),
  };
  const current = prices[weight];
  const buyUrl = (base) => `${base}${base.includes("?") ? "&" : "?"}variant=${weight}`;

  return (
    <div data-testid={`page-product-${product.slug}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 link-underline text-[11px] tracking-overline uppercase text-elle-green"
          data-testid="product-back-shop"
        >
          <ArrowLeft size={12} /> Back to shop
        </Link>
      </div>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Images */}
          <div className="md:col-span-7 space-y-6">
            <Reveal>
              <div className="relative aspect-square bg-elle-sand overflow-hidden">
                <img src={product.image_front} alt={product.name} className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <div className="grid grid-cols-2 gap-6">
              <Reveal delay={0.1}>
                <div className="aspect-square bg-elle-muted overflow-hidden">
                  <img src={product.image_back} alt={`${product.name} story`} className="w-full h-full object-cover" />
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="aspect-square overflow-hidden">
                  <img src={PHOTOGRAPHY.beans} alt="Roasted beans" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            </div>
          </div>

          {/* Sticky info */}
          <div className="md:col-span-5 md:sticky md:top-32">
            <Overline>{product.subtitle}</Overline>
            <h1 className="font-serif-display text-5xl md:text-6xl text-elle-green mt-4 leading-[1.02]">
              {product.name.replace("Ell'e Coffee — ", "")}
            </h1>
            <p className="mt-6 text-[16px] leading-relaxed text-elle-soft">
              {product.long_description}
            </p>

            {/* Specs */}
            <div className="mt-10 grid grid-cols-2 gap-y-5 gap-x-6 border-t border-elle pt-8">
              {[
                ["Origin", product.origin],
                ["Region", product.region],
                ["Altitude", product.altitude],
                ["Bean", product.bean],
                ["Roast", product.roast],
                ["Process", product.process],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] tracking-overline uppercase text-elle-green/60">{k}</div>
                  <div className="mt-1 font-serif-display text-lg text-elle-green">{v}</div>
                </div>
              ))}
            </div>

            {/* Tasting */}
            <div className="mt-10 border-t border-elle pt-8">
              <Overline>Tasting Notes</Overline>
              <div className="flex flex-wrap gap-2 mt-4">
                {product.tasting_notes.map((n) => (
                  <span key={n} className="text-[11px] tracking-overline uppercase border border-elle px-3 py-1.5 text-elle-green">
                    {n}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4 mt-6 text-center">
                <div>
                  <div className="text-[10px] tracking-overline uppercase text-elle-green/60">Aroma</div>
                  <div className="mt-1 font-serif-display italic text-elle-green">{product.aroma}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-overline uppercase text-elle-green/60">Body</div>
                  <div className="mt-1 font-serif-display italic text-elle-green">{product.body}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-overline uppercase text-elle-green/60">Acidity</div>
                  <div className="mt-1 font-serif-display italic text-elle-green">{product.acidity}</div>
                </div>
              </div>
            </div>

            {/* Price + buy */}
            <div className="mt-10 border-t border-elle pt-8">
              <div className="flex items-end justify-between gap-6">
                <div>
                  <Overline>Price</Overline>
                  <div className="font-serif-display text-5xl text-elle-green mt-3" data-testid="product-price">
                    ${current}
                    <span className="text-base text-elle-soft ml-2">USD</span>
                  </div>
                </div>
                <div className="inline-flex border border-elle text-[11px] tracking-overline uppercase" data-testid="product-weight-toggle">
                  {["500g", "1kg"].map((w) => (
                    <button
                      key={w}
                      data-testid={`product-weight-${w}`}
                      onClick={() => setWeight(w)}
                      className={`px-4 py-2.5 transition-colors duration-300 ${
                        weight === w ? "bg-elle-green text-elle-cream" : "text-elle-green hover:bg-elle-muted"
                      }`}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={buyUrl(product.shopify_url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="product-shopify-btn"
                  className="flex-1 text-center inline-flex items-center justify-center gap-2 bg-elle-green text-elle-cream px-8 py-4 text-[11px] tracking-overline uppercase hover:bg-[var(--elle-green-deep)] transition-colors duration-500"
                >
                  Buy on Shopify <ArrowUpRight size={14} />
                </a>
                <a
                  href={buyUrl(product.amazon_url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="product-amazon-btn"
                  className="flex-1 text-center inline-flex items-center justify-center gap-2 bg-elle-red text-elle-cream px-8 py-4 text-[11px] tracking-overline uppercase hover:bg-[var(--elle-red-deep)] transition-colors duration-500"
                >
                  Buy on Amazon <ArrowUpRight size={14} />
                </a>
              </div>
              <p className="mt-4 text-[12px] text-elle-dim">
                External checkout via Shopify or Amazon. Shipping worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PatternStrip height={8} />

      {/* Brewing + Storage */}
      <section className="py-24 md:py-32 bg-elle-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16">
          <Reveal>
            <Overline>Brewing</Overline>
            <h2 className="font-serif-display text-4xl md:text-5xl text-elle-green mt-4 leading-tight">
              Four ways to meet the cup.
            </h2>
            <ul className="mt-10 divide-y divide-elle">
              {product.brewing.map((b, i) => (
                <li key={i} className="py-5 flex items-start gap-6">
                  <div className="font-serif-display text-3xl text-elle-red w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-[15px] text-elle-soft leading-relaxed">{b}</div>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Storage</Overline>
            <h2 className="font-serif-display text-4xl md:text-5xl text-elle-green mt-4 leading-tight">
              Keep the aroma alive.
            </h2>
            <p className="mt-10 text-[16px] leading-relaxed text-elle-soft">
              {product.storage}
            </p>
            <div className="mt-10 aspect-[4/3] overflow-hidden">
              <img src={PHOTOGRAPHY.cherries} alt="Coffee cherries" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Product;
